import React, {
  ChangeEvent,
  FormEvent,
  useEffect,
  useState,
  type FC,
} from "react";
import classes from "./ReviewModal.module.css";
import BackgroundMovieLayout from "./BackgroundMovieLayout";
import { convertMinutesToHoursAndMinutes } from "../Utils/helpers";
import { animate, motion, useAnimationControls } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../Store/hooks";
import { toggleReviewModal } from "../Slices/reviewModalSlice";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { usePostReviewMutation } from "../Slices/httpSlice";
import { toast } from "react-toastify";
import Spinner from "./Loaders/Spinner";
import { openViewReviewModal } from "../Slices/viewReviewModalSlice";
import { logoutUser } from "../Slices/userSlice";
import { useNavigate } from "react-router-dom";

type ReviewModalProps = {
  media_id: any;
  media_type: "movie" | "tv";
  media_name: string;
  windowWidth: number;
  mediaDetails: any;
  mediaReviewsRefetch: any;
  mediaReviews: any;
};

const ReviewModal: FC<ReviewModalProps> = ({
  windowWidth,
  mediaDetails,
  media_id,
  media_type,
  media_name,
  mediaReviews,
  mediaReviewsRefetch,
}) => {
  const controls = useAnimationControls();
  const [reviewStars, setReviewStars] = useState<number>(0);
  const [headline, setHeadline] = useState<string>("");
  const [review, setReview] = useState<string>("");

  const [postReview, { isLoading }] = usePostReviewMutation();

  const variants = {
    hidden: {
      y: window.innerHeight,
      display: "none",
    },
    visible: {
      y: 0,
      display: "block",
    },
  };

  const { reviewModal } = useAppSelector((state) => state.reviewModalSlice);
  useEffect(() => {
    if (reviewModal) {
      setReviewStars(0);
      setHeadline("");
      setReview("");
      controls.start("visible");
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [reviewModal]);

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.userSlice);

  const closeReviewModal = () => {
    controls.start("hidden");
    dispatch(toggleReviewModal());
  };

  const starClicked = (i: number) => {
    setReviewStars(i);
  };
  const navigate=useNavigate();

  const submitHandler = async () => {
    if (
      reviewStars === 0 ||
      headline.trim().length === 0 ||
      review.trim().length === 0
    ) {
      toast.error(
        "Please ensure all required details for the review are filled out!"
      );
      return;
    }
    const response: any = await postReview({
      body: {
        stars: reviewStars,
        headline: headline,
        review: review,
        user_id: user?.id,
        media_id: media_id,
        media_type: media_type,
        media_name: media_name,
        media_backdrop: mediaDetails?.result?.backdrop_path,
        media_poster: mediaDetails?.result?.poster_path,
        // media_release_year: new Date(
        //   mediaDetails?.result?.release_date
        // ).getFullYear(),

        media_release_year:
          media_type === "movie"
            ? new Date(mediaDetails?.result?.release_date).getFullYear()
            : new Date(mediaDetails?.result?.first_air_date).getFullYear(),
      },
      token: user?.token,
    });
    console.log(response, "response....");

    if (response?.error) {
      console.log(response.error, "error");
      if (response?.error?.status === 403) {
        toast.error("Your session has expired! Login Again!");
        dispatch(logoutUser(null));
        navigate("/");

      } else {
        toast.error(
          "Something Went Wrong!" ||
          response?.error?.data?.error?.error ||
          response?.error?.data?.error ||
          response?.error?.data?.message ||
          response?.error?.message
        );
      }
    } else {
      setReviewStars(0);
      setHeadline("");
      setReview("");
      mediaReviewsRefetch();
      // closeReviewModal();
      toast.success("Review Added Successfully!");
    }
  };

  const handleReview = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setReview(e.target.value);
  };

  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    setStartPosition({ x: event.clientX, y: event.clientY });
    setIsDragging(false);
  };

  const handleMouseUp = (
    event: React.MouseEvent<HTMLButtonElement>,
    reviewId: number
  ) => {
    const distance = Math.sqrt(
      Math.pow(event.clientX - startPosition.x, 2) +
      Math.pow(event.clientY - startPosition.y, 2)
    );
    if (distance < 5 && !isDragging) {
      closeReviewModal();
      setTimeout(() => {
        dispatch(openViewReviewModal({ reviewId: reviewId }));
      }, 500);
    }
  };

  const handleMouseMove = () => {
    setIsDragging(true);
  };

  return (
    <motion.div
      className={classes.modal}
      variants={variants}
      initial="hidden"
      animate={controls}
      transition={{
        duration: 0.5,
        type: "spring",
        damping: 10,
        stiffness: 100,
      }}
    >
      <BackgroundMovieLayout
        windowWidth={windowWidth}
        backdropImage={
          !mediaDetails?.result ? "error" : mediaDetails?.result?.backdrop_path
        }
      >
        <div className={classes.review_navbar}>
          <button
            className={classes.close_btn}
            type="button"
            onClick={closeReviewModal}
          >
            <i className={`pi pi-times ${classes.close_icon}`} />
          </button>
        </div>
        <div className={classes.movie_details_grid}>
          <div className={classes.movie_details_brief}>
            {media_type === "movie" ? (
              <p className={classes.title}>
                {mediaDetails?.result?.title ?? mediaDetails?.result?.name}{" "}
                <span className={classes.span}>
                  ({new Date(mediaDetails?.result?.release_date).getFullYear()})
                </span>
              </p>
            ) : (
              <p className={classes.title}>
                {mediaDetails?.result?.title ?? mediaDetails?.result?.name}{" "}
                <span className={classes.span}>
                  (
                  {new Date(mediaDetails?.result?.first_air_date).getFullYear()}
                  {mediaDetails?.result?.status === "Ended"
                    ? ` - ${new Date(
                      mediaDetails?.result?.last_air_date
                    ).getFullYear()}`
                    : ` - `}
                  )
                </span>
              </p>
            )}

            <div className={classes.info_container}>
              {mediaDetails?.result?.status === "Released" ? (
                <>
                  <div className={classes.duration}>
                    <i className={`pi pi-circle-fill ${classes.bullet}`} />

                    <p className={classes.clock_time}>
                      {convertMinutesToHoursAndMinutes(
                        mediaDetails?.result?.runtime
                      )}
                    </p>
                  </div>
                  <div className={classes.rating}>
                    <img
                      src="/assets/images/star.png"
                      className={classes.star}
                      alt="star"
                    />
                    <p className={classes.rating_number}>
                      {mediaDetails?.result?.vote_average}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className={classes.duration}>
                    <i className={`pi pi-circle-fill ${classes.bullet}`} />
                    <p className={classes.clock_time}>
                      {mediaDetails?.result?.status}
                    </p>
                  </div>
                  <div className={classes.rating}>
                    <img
                      src="/assets/images/star.png"
                      className={classes.star}
                      alt="star"
                    />
                    <p className={classes.rating_number}>
                      {mediaDetails?.result?.vote_average}
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className={classes.overview_section}>
              <p className={classes.overview}>
                {mediaDetails?.result?.overview}
              </p>
            </div>
          </div>
        </div>

        <form className={classes.form_container}>
          <div className={classes.review_star_container}>
            <div className={classes.review_stars}>
              {Array.from({ length: 10 }).map((_, idx: number) => {
                return (
                  <>
                    <motion.i
                      whileHover={{ color: "yellow" }}
                      onClick={() => starClicked(idx + 1)}
                      className={`pi ${reviewStars <= idx ? `pi-star` : `pi-star-fill`
                        } ${reviewStars <= idx
                          ? classes.star_icon
                          : classes.star_filled_icon
                        }`}
                    ></motion.i>
                  </>
                );
              })}
            </div>
            {reviewStars > 0 && (
              <p className={classes.review_given}>
                You rated this {reviewStars}/10
              </p>
            )}
          </div>

          <div className={classes.inputs}>
            <InputText
              type="text"
              className={`p-inputtext-lg ${classes.input}`}
              placeholder="Write a headline for your review here"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
            />

            <InputTextarea
              className={`p-inputtext-lg ${classes.input} ${classes.textarea}`}
              placeholder="Write your review here"
              rows={6}
              cols={30}
              value={review}
              onChange={handleReview}
            // onKeyDown={(e) => handleKeyDown(e)}
            />
          </div>

          <button
            type="button"
            disabled={isLoading}
            onClick={() => submitHandler()}
            className={classes.review_submit_btn}
          >
            {isLoading ? (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Spinner theme="light" />
              </div>
            ) : (
              "Submit"
            )}
          </button>
        </form>

        <div className={classes.section_drag_cards}>
          <div className={classes.section_cards}>
            <div className={classes.category_title_container}>
              <div className={classes.title_container}>
                <p className={classes.category_title}>User Reviews</p>
                <p className={classes.images_length}>
                  {mediaReviews?.result.length}
                </p>
                <i className={`pi pi-chevron-right ${classes.chevron}`} />
              </div>
            </div>

            <motion.div
              className={classes.section_review_card}
              style={{
                gridTemplateColumns: `repeat(${mediaReviews?.result.length}, 320px)`,
              }}
              drag="x"
              dragConstraints={{
                top: 0,
                left: -(windowWidth <= 800
                  ? 10 +
                  mediaReviews?.result.length * 320 +
                  mediaReviews?.result.length * 20 +
                  mediaReviews?.result.length * 1 -
                  windowWidth
                  : window.innerWidth * 0.05 +
                  mediaReviews?.result.length * 320 +
                  mediaReviews?.result.length * 20 +
                  mediaReviews?.result.length * 1 -
                  windowWidth),
                right: 0,
                bottom: 0,
              }}
              transition={{
                type: "spring",
                damping: 100,
                stiffness: 100,
              }}
            >
              {mediaReviews?.result?.map((data: any, idx: number) => {
                return (
                  <button
                    className={classes.review_card}
                    key={idx}
                    onMouseDown={(event) => handleMouseDown(event)}
                    onMouseUp={(event) => handleMouseUp(event, data?.id)}
                    onMouseMove={handleMouseMove}
                  >
                    <div className={classes.review_rating_container}>
                      <img
                        src="/assets/images/star.png"
                        alt="star"
                        className={classes.review_star}
                      />
                      <div className={classes.review_rating_sub_container}>
                        <p className={classes.review_rating}>
                          {data?.stars_rated}
                        </p>
                        <span className={classes.slash}>/</span>
                        <p className={classes.review_rating_out_of}>10</p>
                      </div>
                    </div>

                    <div className={classes.review_intro_container}>
                      <p className={classes.review_headline}>
                        {JSON.parse(data?.review_headline)}
                      </p>
                      <div className={classes.review_by}>
                        <p className={classes.username}>{data?.username}</p>
                        <p className={classes.review_date}>
                          {new Date(data?.date_time).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            }
                          )}
                        </p>
                      </div>
                    </div>

                    <div className={classes.review_container}>
                      <p
                        className={classes.review}
                        dangerouslySetInnerHTML={{
                          __html: JSON.parse(data?.review).replace(
                            /\n/g,
                            "<br />"
                          ),
                        }}
                      ></p>
                    </div>
                  </button>
                );
              })}
            </motion.div>
          </div>
        </div>
      </BackgroundMovieLayout>
    </motion.div>
  );
};

export default ReviewModal;
